import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Input, Spinner } from '@/components';
import { AudioPreviewButton } from '@/components/admin/AudioPreviewButton';
import { ResourceCardColorPicker } from '@/components/admin/ResourceCardColorPicker';
import { StorybookDevicePreviews } from '@/components/admin/StorybookDevicePreviews';
import type {
  AdminStorybook,
  StorybookMatchAudioResponse,
  StorybookMatchImagesResponse,
  StorybookMediaUpload,
} from '@/lib/admin-types';
import { apiJson, getApiErrorMessage } from '@/lib/api';
import { expandAudioUploads } from '@/lib/flashcard-audio-zip';
import {
  resourceCardColorPayload,
  type ResourceCardColorOption,
} from '@/lib/resource-card-colors';

type DraftPage = {
  localId: string;
  id?: number;
  sort_order: number;
  title: string;
  audio_optional: boolean;
  image_cdn_key: string | null;
  image_url: string | null;
  image_tablet_cdn_key: string | null;
  image_tablet_url: string | null;
  sound_adult_cdn_key: string | null;
  sound_adult_url: string | null;
  sound_children_cdn_key: string | null;
  sound_children_url: string | null;
};

type UploadedMedia = {
  original_filename: string;
  cdn_key: string;
  url: string;
  voice?: string | null;
};

function newLocalId(): string {
  return `local-${Math.random().toString(36).slice(2, 10)}`;
}

function bookToDraft(book: AdminStorybook): DraftPage[] {
  return book.pages.map((page, idx) => ({
    localId: `page-${page.id || idx}`,
    id: page.id,
    sort_order: page.sort_order ?? idx,
    title: page.title || '',
    audio_optional: Boolean(page.audio_optional),
    image_cdn_key: page.image_cdn_key,
    image_url: page.image_url,
    image_tablet_cdn_key: page.image_tablet_cdn_key,
    image_tablet_url: page.image_tablet_url,
    sound_adult_cdn_key: page.sound_adult_cdn_key,
    sound_adult_url: page.sound_url,
    sound_children_cdn_key: page.sound_children_cdn_key,
    sound_children_url: page.sound_children_url,
  }));
}

function draftToPayload(pages: DraftPage[], singleVoice: boolean) {
  return {
    pages: pages.map((page, idx) => {
      const adult = page.sound_adult_cdn_key;
      const children = page.sound_children_cdn_key;
      const single = adult || children;
      return {
        id: page.id,
        sort_order: idx,
        title: page.title.trim() || `page-${idx + 1}`,
        image_cdn_key: page.image_cdn_key,
        image_tablet_cdn_key: page.image_tablet_cdn_key,
        sound_adult_cdn_key: singleVoice ? single : adult,
        sound_children_cdn_key: singleVoice ? single : children,
        audio_optional: page.audio_optional,
      };
    }),
  };
}

async function uploadImage(
  bookId: number,
  file: File,
  kind: 'images' | 'images_tablet' | 'covers' = 'images',
  originalFilename?: string
): Promise<StorybookMediaUpload> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  if (originalFilename) form.append('original_filename', originalFilename);
  return apiJson<StorybookMediaUpload>(`/v1/admin/storybooks/${bookId}/images/upload`, {
    method: 'POST',
    body: form,
  });
}

async function uploadAudio(
  bookId: number,
  file: File,
  opts?: { voice?: string; original_filename?: string }
): Promise<StorybookMediaUpload> {
  const form = new FormData();
  form.append('file', file);
  form.append('convert_sync', 'true');
  if (opts?.voice) form.append('voice', opts.voice);
  if (opts?.original_filename) form.append('original_filename', opts.original_filename);
  return apiJson<StorybookMediaUpload>(`/v1/admin/storybooks/${bookId}/audio/upload`, {
    method: 'POST',
    body: form,
  });
}

function StorybookDetailPage() {
  const { id } = Route.useParams();
  const bookId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: book, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'storybooks', bookId],
    queryFn: () => apiJson<AdminStorybook>(`/v1/admin/storybooks/${bookId}`),
    enabled: Number.isFinite(bookId),
  });

  const [name, setName] = useState('');
  const [audioVoices, setAudioVoices] = useState<'dual' | 'single'>('dual');
  const [requiresSubscription, setRequiresSubscription] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('');
  const [textColor, setTextColor] = useState('');
  const [badgeBackground, setBadgeBackground] = useState('');
  const [pages, setPages] = useState<DraftPage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadedMedia[]>([]);
  const [uploadedAudio, setUploadedAudio] = useState<UploadedMedia[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const hydratedBookIdRef = useRef<number | null>(null);

  useEffect(() => {
    hydratedBookIdRef.current = null;
  }, [bookId]);

  useEffect(() => {
    if (!book) return;
    if (hydratedBookIdRef.current === book.id) return;
    hydratedBookIdRef.current = book.id;
    setName(book.name);
    setAudioVoices(book.audio_voices === 'single' ? 'single' : 'dual');
    setRequiresSubscription(book.requires_subscription);
    setBackgroundColor(book.background_color || '');
    setTextColor(book.text_color || '');
    setBadgeBackground(book.badge_background || '');
    const draft = bookToDraft(book);
    setPages(draft);
    setSelectedId(draft[0]?.localId ?? null);
  }, [book]);

  const singleVoice = audioVoices === 'single';
  const selected = pages.find((p) => p.localId === selectedId) || null;

  function onPickCardColor(option: ResourceCardColorOption) {
    const payload = resourceCardColorPayload(option);
    setBackgroundColor(payload.background_color);
    setTextColor(payload.text_color);
    setBadgeBackground(payload.badge_background);
  }

  const audioUrlByCdnKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of uploadedAudio) {
      if (a.cdn_key && a.url) map.set(a.cdn_key, a.url);
    }
    return map;
  }, [uploadedAudio]);

  function resolveSoundUrl(
    storedUrl: string | null,
    cdnKey: string | null
  ): string | null {
    return storedUrl || (cdnKey ? audioUrlByCdnKey.get(cdnKey) ?? null : null);
  }

  const saveMeta = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiJson<AdminStorybook>(`/v1/admin/storybooks/${bookId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (saved) => {
      qc.setQueryData(['admin', 'storybooks', bookId], saved);
      void qc.invalidateQueries({ queryKey: ['admin', 'storybooks'], exact: true });
      setMessage(saved.status === 'live' ? 'Published live' : 'Settings saved');
    },
    onError: (err) => setMessage(getApiErrorMessage(err)),
  });

  const savePages = useMutation({
    mutationFn: () =>
      apiJson<AdminStorybook>(`/v1/admin/storybooks/${bookId}/pages`, {
        method: 'PUT',
        body: JSON.stringify(draftToPayload(pages, singleVoice)),
      }),
    onSuccess: (saved) => {
      hydratedBookIdRef.current = null;
      qc.setQueryData(['admin', 'storybooks', bookId], saved);
      void qc.invalidateQueries({ queryKey: ['admin', 'storybooks'], exact: true });
      setMessage('Pages saved');
    },
    onError: (err) => setMessage(getApiErrorMessage(err)),
  });

  const deleteBook = useMutation({
    mutationFn: () =>
      apiJson<void>(`/v1/admin/storybooks/${bookId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'storybooks'] });
      void navigate({ to: '/admin/storybooks' });
    },
    onError: (err) => setMessage(getApiErrorMessage(err)),
  });

  function updatePage(localId: string, patch: Partial<DraftPage>) {
    setPages((prev) =>
      prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p))
    );
  }

  function addPage() {
    const page: DraftPage = {
      localId: newLocalId(),
      sort_order: pages.length,
      title: `page-${pages.length + 1}`,
      audio_optional: false,
      image_cdn_key: null,
      image_url: null,
      image_tablet_cdn_key: null,
      image_tablet_url: null,
      sound_adult_cdn_key: null,
      sound_adult_url: null,
      sound_children_cdn_key: null,
      sound_children_url: null,
    };
    setPages((prev) => [...prev, page]);
    setSelectedId(page.localId);
  }

  function movePage(index: number, delta: -1 | 1) {
    setPages((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next.map((page, sort_order) => ({ ...page, sort_order }));
    });
  }

  async function onUploadImages(fileList: FileList | null) {
    if (!fileList?.length) return;
    setBusy('Uploading images…');
    setMessage(null);
    try {
      const uploaded: UploadedMedia[] = [];
      for (const file of Array.from(fileList)) {
        const res = await uploadImage(bookId, file, 'images', file.name);
        uploaded.push({
          original_filename: res.original_filename,
          cdn_key: res.cdn_key,
          url: res.url,
        });
      }
      setUploadedImages((prev) => [...prev, ...uploaded]);
      setMessage(`Uploaded ${uploaded.length} image(s)`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onAssignImageToSelected(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file || !selected) return;
    setBusy('Uploading image to selected page…');
    setMessage(null);
    try {
      const res = await uploadImage(bookId, file, 'images', file.name);
      const media: UploadedMedia = {
        original_filename: res.original_filename,
        cdn_key: res.cdn_key,
        url: res.url,
      };
      setUploadedImages((prev) => [...prev, media]);
      updatePage(selected.localId, {
        image_cdn_key: media.cdn_key,
        image_url: media.url,
      });
      setMessage(`Assigned image to ${selected.title || 'selected page'}`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onMatchImages() {
    if (!pages.length || !uploadedImages.length) return;
    setBusy('Matching images…');
    setMessage(null);
    try {
      const match = await apiJson<StorybookMatchImagesResponse>(
        `/v1/admin/storybooks/${bookId}/match-images`,
        {
          method: 'POST',
          body: JSON.stringify({
            pages: pages.map((p) => ({ title: p.title })),
            image_filenames: uploadedImages.map((i) => i.original_filename),
          }),
        }
      );
      const byName = new Map(uploadedImages.map((i) => [i.original_filename, i]));
      setPages((prev) =>
        prev.map((page, idx) => {
          const assignment = match.assignments.find((a) => a.page_index === idx);
          if (!assignment?.image_filename) return page;
          const img = byName.get(assignment.image_filename);
          if (!img) return page;
          return {
            ...page,
            image_cdn_key: img.cdn_key,
            image_url: img.url,
          };
        })
      );
      setMessage(
        `Matched images. Unmatched: ${match.unmatched_images.length || 0}`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onUploadAudio(fileList: FileList | null) {
    if (!fileList?.length) return;
    setBusy('Uploading + converting audio…');
    setMessage(null);
    try {
      const expanded = await expandAudioUploads(Array.from(fileList));
      const uploaded: UploadedMedia[] = [];
      for (let i = 0; i < expanded.length; i++) {
        const item = expanded[i]!;
        setBusy(`Uploading + converting audio… (${i + 1}/${expanded.length})`);
        const res = await uploadAudio(bookId, item.file, {
          voice: item.voice ?? undefined,
          original_filename: item.relativePath,
        });
        uploaded.push({
          original_filename: res.original_filename || item.relativePath,
          cdn_key: res.cdn_key,
          url: res.url,
          voice: res.voice ?? item.voice,
        });
      }
      setUploadedAudio((prev) => [...prev, ...uploaded]);
      setMessage(`Uploaded ${uploaded.length} audio file(s)`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onMatchAudio() {
    if (!pages.length || !uploadedAudio.length) return;
    setBusy('Matching audio…');
    setMessage(null);
    try {
      const match = await apiJson<StorybookMatchAudioResponse>(
        `/v1/admin/storybooks/${bookId}/match-audio`,
        {
          method: 'POST',
          body: JSON.stringify({
            pages: pages.map((p) => ({ title: p.title })),
            audio_filenames: uploadedAudio.map((a) => a.original_filename),
          }),
        }
      );
      const byName = new Map(uploadedAudio.map((a) => [a.original_filename, a]));
      setPages((prev) =>
        prev.map((page, idx) => {
          const assignment = match.assignments.find((a) => a.page_index === idx);
          if (!assignment) return page;
          const adult = assignment.sound_adult_filename
            ? byName.get(assignment.sound_adult_filename)
            : null;
          const child = assignment.sound_children_filename
            ? byName.get(assignment.sound_children_filename)
            : null;
          return {
            ...page,
            sound_adult_cdn_key: adult?.cdn_key ?? page.sound_adult_cdn_key,
            sound_adult_url: adult?.url ?? page.sound_adult_url,
            sound_children_cdn_key: child?.cdn_key ?? page.sound_children_cdn_key,
            sound_children_url: child?.url ?? page.sound_children_url,
          };
        })
      );
      setMessage(
        `Matched audio. Unmatched: ${match.unmatched_audio.length || 0}`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onReplacePageAudio(
    localId: string,
    voice: 'adult' | 'children',
    fileList: FileList | null
  ) {
    const file = fileList?.[0];
    if (!file) return;
    const voiceLabel =
      voice === 'adult' ? (singleVoice ? 'audio' : 'adult audio') : 'child audio';
    setBusy(`Replacing ${voiceLabel}…`);
    setMessage(null);
    try {
      const res = await uploadAudio(bookId, file, {
        voice,
        original_filename: file.name,
      });
      const media: UploadedMedia = {
        original_filename: res.original_filename,
        cdn_key: res.cdn_key,
        url: res.url,
        voice: res.voice ?? voice,
      };
      setUploadedAudio((prev) => [...prev, media]);
      if (voice === 'adult') {
        updatePage(localId, {
          sound_adult_cdn_key: media.cdn_key,
          sound_adult_url: media.url,
        });
      } else {
        updatePage(localId, {
          sound_children_cdn_key: media.cdn_key,
          sound_children_url: media.url,
        });
      }
      setMessage(`${voiceLabel} updated — save pages to persist`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    setBusy('Publishing…');
    setMessage(null);
    try {
      await savePages.mutateAsync();
      await saveMeta.mutateAsync({ status: 'live' });
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError || !book) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error)?.message || 'Not found'}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/storybooks" className="text-sm text-primary hover:underline">
            ← Storybooks
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{book.name}</h1>
            <Badge variant={book.status === 'live' ? 'success' : 'warning'}>
              {book.status}
            </Badge>
            <Badge>{book.mode}</Badge>
            <Badge>audio: {audioVoices}</Badge>
            {requiresSubscription ? <Badge variant="warning">Premium</Badge> : null}
          </div>
          <p className="mt-1 font-mono text-xs text-gray-500">
            {book.slug} · v{book.content_version}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            disabled={!!busy || savePages.isPending}
            onClick={() => savePages.mutate()}
          >
            {savePages.isPending ? 'Saving…' : 'Save pages'}
          </Button>
          {book.status === 'live' ? (
            <Button
              variant="secondary"
              disabled={saveMeta.isPending}
              onClick={() => saveMeta.mutate({ status: 'draft' })}
            >
              Unpublish
            </Button>
          ) : (
            <Button disabled={!!busy || savePages.isPending} onClick={() => void publish()}>
              Publish
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={deleteBook.isPending}
            onClick={() => {
              if (window.confirm(`Delete storybook “${book.name}”? This cannot be undone.`)) {
                deleteBook.mutate();
              }
            }}
          >
            Delete
          </Button>
        </div>
      </div>

      {(message || busy) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {busy || message}
        </div>
      )}

      {book.validation_errors?.length ? (
        <Card className="border-amber-200 bg-amber-50">
          <h2 className="text-sm font-medium text-amber-900">Publish blockers</h2>
          <ul className="mt-2 list-disc pl-5 text-sm text-amber-900">
            {book.validation_errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card className="border-emerald-200 bg-emerald-50 text-sm text-emerald-900">
          Ready to publish — validation passed on last saved pages.
        </Card>
      )}

      <Card className="space-y-3">
        <h2 className="text-sm font-medium">Book meta</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-xs text-gray-600">
            Name
            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-xs text-gray-600">
            Audio voices
            <select
              className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              value={audioVoices}
              onChange={(e) => setAudioVoices(e.target.value as 'dual' | 'single')}
            >
              <option value="dual">Dual</option>
              <option value="single">Single</option>
            </select>
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={requiresSubscription}
              onChange={(e) => setRequiresSubscription(e.target.checked)}
            />
            Subscribers only
          </label>
          <Button
            disabled={saveMeta.isPending || !name.trim()}
            onClick={() =>
              saveMeta.mutate({
                name: name.trim(),
                audio_voices: audioVoices,
                requires_subscription: requiresSubscription,
                background_color: backgroundColor,
                text_color: textColor,
                badge_background: badgeBackground,
              })
            }
          >
            Save settings
          </Button>
        </div>
        <ResourceCardColorPicker
          value={backgroundColor}
          onChange={onPickCardColor}
          disabled={saveMeta.isPending}
        />
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium">Media upload</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <p className="text-sm text-gray-700">Page images</p>
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={!!busy}
              onChange={(e) => {
                void onUploadImages(e.target.files);
                e.target.value = '';
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                className="!h-8 !text-xs"
                disabled={!!busy || !uploadedImages.length || !pages.length}
                onClick={() => void onMatchImages()}
              >
                Match images by stem
              </Button>
              <label
                className={`inline-flex h-8 cursor-pointer items-center rounded-md border border-gray-300 px-2.5 text-xs font-medium hover:bg-gray-50 ${
                  !selected || busy ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                Assign to selected page
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={!selected || !!busy}
                  onChange={(e) => {
                    void onAssignImageToSelected(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {uploadedImages.length} image(s) in session buffer
              {selected ? ` · selected: ${selected.title || 'untitled'}` : ''}
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-700">Audio (files or zip)</p>
            <input
              type="file"
              accept="audio/*,.wav,.m4a,.amr,.mp3,.zip,application/zip"
              multiple
              disabled={!!busy}
              onChange={(e) => {
                void onUploadAudio(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              className="!h-8 !text-xs"
              disabled={!!busy || !uploadedAudio.length || !pages.length}
              onClick={() => void onMatchAudio()}
            >
              Match audio by stem
            </Button>
            <p className="text-xs text-gray-500">
              {uploadedAudio.length} audio file(s) in session buffer. Dual voice zip layout:{' '}
              <code>kid/</code> + <code>kristina/</code>.
            </p>
          </div>
        </div>
      </Card>

      <Card className="space-y-3 overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium">Pages</h2>
          <Button className="!h-8 !text-xs" onClick={addPage}>
            + Page
          </Button>
        </div>
        {pages.length === 0 ? (
          <p className="text-sm text-gray-500">No pages yet. Add a page to get started.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-2 py-2">#</th>
                <th className="px-2 py-2">Title</th>
                <th className="px-2 py-2">Image</th>
                <th className="px-2 py-2">{singleVoice ? 'Audio' : 'Adult'}</th>
                {!singleVoice ? <th className="px-2 py-2">Child</th> : null}
                <th className="px-2 py-2">Optional</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {pages.map((page, idx) => (
                <tr
                  key={page.localId}
                  className={`border-t border-gray-100 ${
                    selectedId === page.localId ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => setSelectedId(page.localId)}
                >
                  <td className="px-2 py-2 text-xs text-gray-400">{idx + 1}</td>
                  <td className="px-2 py-2">
                    <input
                      className="h-8 w-36 rounded border border-gray-300 px-2 text-sm"
                      value={page.title}
                      onChange={(e) => updatePage(page.localId, { title: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col items-start gap-1">
                      {page.image_url ? (
                        <img
                          src={page.image_url}
                          alt=""
                          className="h-12 w-12 rounded object-cover"
                        />
                      ) : (
                        <span className="text-xs text-red-600">missing</span>
                      )}
                      <label
                        className={`text-xs text-primary hover:underline ${
                          busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                        }`}
                      >
                        {page.image_cdn_key ? 'Replace' : 'Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={!!busy}
                          onChange={(e) => {
                            setSelectedId(page.localId);
                            void (async () => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setBusy('Uploading image…');
                              try {
                                const res = await uploadImage(
                                  bookId,
                                  file,
                                  'images',
                                  file.name
                                );
                                updatePage(page.localId, {
                                  image_cdn_key: res.cdn_key,
                                  image_url: res.url,
                                });
                                setUploadedImages((prev) => [
                                  ...prev,
                                  {
                                    original_filename: res.original_filename,
                                    cdn_key: res.cdn_key,
                                    url: res.url,
                                  },
                                ]);
                                setMessage('Image updated — save pages to persist');
                              } catch (err) {
                                setMessage(getApiErrorMessage(err));
                              } finally {
                                setBusy(null);
                              }
                            })();
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col items-start gap-1">
                      <AudioPreviewButton
                        url={resolveSoundUrl(page.sound_adult_url, page.sound_adult_cdn_key)}
                        cdnKey={page.sound_adult_cdn_key}
                        label={`${page.title || 'page'} ${singleVoice ? 'audio' : 'adult audio'}`}
                      />
                      <label
                        className={`text-xs text-primary hover:underline ${
                          busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                        }`}
                      >
                        {page.sound_adult_cdn_key ? 'Replace' : 'Upload'}
                        <input
                          type="file"
                          accept="audio/*,.wav,.m4a,.amr,.mp3"
                          className="hidden"
                          disabled={!!busy}
                          onChange={(e) => {
                            void onReplacePageAudio(page.localId, 'adult', e.target.files);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </td>
                  {!singleVoice ? (
                    <td className="px-2 py-2">
                      <div className="flex flex-col items-start gap-1">
                        <AudioPreviewButton
                          url={resolveSoundUrl(
                            page.sound_children_url,
                            page.sound_children_cdn_key
                          )}
                          cdnKey={page.sound_children_cdn_key}
                          label={`${page.title || 'page'} child audio`}
                        />
                        <label
                          className={`text-xs text-primary hover:underline ${
                            busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                          }`}
                        >
                          {page.sound_children_cdn_key ? 'Replace' : 'Upload'}
                          <input
                            type="file"
                            accept="audio/*,.wav,.m4a,.amr,.mp3"
                            className="hidden"
                            disabled={!!busy}
                            onChange={(e) => {
                              void onReplacePageAudio(
                                page.localId,
                                'children',
                                e.target.files
                              );
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    </td>
                  ) : null}
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={page.audio_optional}
                      onChange={(e) =>
                        updatePage(page.localId, { audio_optional: e.target.checked })
                      }
                      title="Audio optional"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        disabled={idx === 0}
                        aria-label="Move page up"
                        onClick={(e) => {
                          e.stopPropagation();
                          movePage(idx, -1);
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="rounded px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        disabled={idx === pages.length - 1}
                        aria-label="Move page down"
                        onClick={(e) => {
                          e.stopPropagation();
                          movePage(idx, 1);
                        }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPages((prev) => prev.filter((p) => p.localId !== page.localId));
                          if (selectedId === page.localId) setSelectedId(null);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {pages.length > 0 ? (
        <Card>
          <StorybookDevicePreviews
            pages={pages.map((page) => ({
              localId: page.localId,
              title: page.title,
              imageUrl: page.image_url,
            }))}
            selectedId={selectedId}
            onSelectPage={setSelectedId}
          />
        </Card>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/storybooks/$id')({
  component: StorybookDetailPage,
});
